import { v4 as uuidv4 } from 'uuid';
import { 
  SupportTicket, 
  SupportThread, 
  SupportMessage, 
  SupportContact,
  SupportAgent,
  SupportNote,
  TicketStatus,
  ChannelType
} from '../types/support-ticket';
import { 
  mockAgents, 
  mockContacts, 
  mockMessages, 
  mockThreads, 
  mockTickets,
  mockNotes
} from '../mockData/supportData';

// Clone the mock data to work with
let agents = [...mockAgents];
let contacts = [...mockContacts];
let threads = [...mockThreads];
let messages = [...mockMessages];
let tickets = [...mockTickets];
let notes = [...mockNotes];

// Simulated file storage
interface FileStorage {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  uploaded_at: string;
}
let fileStorage: FileStorage[] = [];

// Support Service
const supportService = {
  // Contacts
  getContacts: async (): Promise<SupportContact[]> => {
    return contacts;
  },

  getContactById: async (id: string): Promise<SupportContact | undefined> => {
    return contacts.find(contact => contact.id === id);
  },

  createContact: async (contact: Omit<SupportContact, "id" | "created_at">): Promise<SupportContact> => {
    const newContact: SupportContact = {
      id: `contact-${uuidv4()}`,
      ...contact,
      created_at: new Date().toISOString()
    };
    contacts.push(newContact);
    return newContact;
  },

  // Agents
  getAgents: async (): Promise<SupportAgent[]> => {
    return agents;
  },

  getAgentById: async (id: string): Promise<SupportAgent | undefined> => {
    return agents.find(agent => agent.id === id);
  },

  // Threads
  getThreads: async (params?: { 
    contactId?: string; 
    isActive?: boolean;
    channel?: ChannelType;
  }): Promise<SupportThread[]> => {
    let filteredThreads = [...threads];
    
    if (params?.contactId) {
      filteredThreads = filteredThreads.filter(thread => thread.contact_id === params.contactId);
    }
    
    if (params?.isActive !== undefined) {
      filteredThreads = filteredThreads.filter(thread => thread.is_active === params.isActive);
    }
    
    if (params?.channel) {
      filteredThreads = filteredThreads.filter(thread => thread.channel === params.channel);
    }
    
    return filteredThreads.sort((a, b) => 
      new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
    );
  },

  getThreadById: async (id: string): Promise<SupportThread | undefined> => {
    return threads.find(thread => thread.id === id);
  },

  createThread: async (thread: Omit<SupportThread, "id" | "created_at" | "last_message_at" | "is_active">): Promise<SupportThread> => {
    const now = new Date().toISOString();
    const newThread: SupportThread = {
      id: `thread-${uuidv4()}`,
      ...thread,
      last_message_at: now,
      is_active: true,
      created_at: now
    };
    threads.push(newThread);
    return newThread;
  },

  closeThread: async (id: string): Promise<SupportThread | undefined> => {
    const threadIndex = threads.findIndex(thread => thread.id === id);
    if (threadIndex === -1) return undefined;
    
    threads[threadIndex] = {
      ...threads[threadIndex],
      is_active: false
    };
    
    return threads[threadIndex];
  },

  // Messages
  getMessagesByThreadId: async (threadId: string): Promise<SupportMessage[]> => {
    return messages
      .filter(message => message.thread_id === threadId)
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  },

  createMessage: async (message: Omit<SupportMessage, "id" | "created_at">): Promise<SupportMessage> => {
    const now = new Date().toISOString();
    const newMessage: SupportMessage = {
      id: `msg-${uuidv4()}`,
      ...message,
      created_at: now
    };
    
    // Add message to the collection
    messages.push(newMessage);
    
    // Update thread's last_message_at
    const threadIndex = threads.findIndex(thread => thread.id === message.thread_id);
    if (threadIndex !== -1) {
      threads[threadIndex] = {
        ...threads[threadIndex],
        last_message_at: now
      };
    }
    
    return newMessage;
  },
  
  // Message attachments
  uploadAttachment: async (file: File): Promise<{ id: string; filename: string; url: string; type: string }> => {
    // Simulate file upload
    return new Promise((resolve) => {
      setTimeout(() => {
        const fileId = uuidv4();
        const fileUrl = URL.createObjectURL(file);
        
        // Store file metadata
        fileStorage.push({
          id: fileId,
          name: file.name,
          type: file.type,
          size: file.size,
          url: fileUrl,
          uploaded_at: new Date().toISOString()
        });
        
        resolve({
          id: fileId,
          filename: file.name,
          url: fileUrl,
          type: file.type
        });
      }, 500);
    });
  },
  
  addAttachmentToMessage: async (
    messageId: string, 
    attachment: { id: string; filename: string; url: string; type: string }
  ): Promise<SupportMessage | undefined> => {
    const messageIndex = messages.findIndex(message => message.id === messageId);
    if (messageIndex === -1) return undefined;
    
    const message = messages[messageIndex];
    
    // Initialize attachments array if it doesn't exist
    if (!message.attachments) {
      message.attachments = [];
    }
    
    // Add the attachment
    message.attachments.push(attachment);
    
    return message;
  },

  // Tickets
  getTickets: async (params?: {
    contactId?: string;
    agentId?: string;
    status?: TicketStatus;
  }): Promise<SupportTicket[]> => {
    let filteredTickets = [...tickets];
    
    if (params?.contactId) {
      filteredTickets = filteredTickets.filter(ticket => ticket.contact_id === params.contactId);
    }
    
    if (params?.agentId) {
      filteredTickets = filteredTickets.filter(ticket => ticket.agent_id === params.agentId);
    }
    
    if (params?.status) {
      filteredTickets = filteredTickets.filter(ticket => ticket.status === params.status);
    }
    
    return filteredTickets.sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  },

  getTicketById: async (id: string): Promise<SupportTicket | undefined> => {
    return tickets.find(ticket => ticket.id === id);
  },

  createTicket: async (ticket: Omit<SupportTicket, "id" | "created_at" | "updated_at">): Promise<SupportTicket> => {
    const now = new Date().toISOString();
    const newTicket: SupportTicket = {
      id: `ticket-${uuidv4()}`,
      ...ticket,
      created_at: now,
      updated_at: now
    };
    tickets.push(newTicket);
    return newTicket;
  },

  updateTicket: async (id: string, updates: Partial<Omit<SupportTicket, "id" | "created_at" | "updated_at">>): Promise<SupportTicket | undefined> => {
    const ticketIndex = tickets.findIndex(ticket => ticket.id === id);
    if (ticketIndex === -1) return undefined;
    
    tickets[ticketIndex] = {
      ...tickets[ticketIndex],
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    return tickets[ticketIndex];
  },

  createTicketFromThread: async (threadId: string, ticketData: Partial<Omit<SupportTicket, "id" | "created_at" | "updated_at" | "thread_id" | "contact_id">>): Promise<SupportTicket | undefined> => {
    const thread = await supportService.getThreadById(threadId);
    if (!thread) return undefined;
    
    // Get all messages for context
    const threadMessages = await supportService.getMessagesByThreadId(threadId);
    const firstMessage = threadMessages[0];
    
    if (!firstMessage) return undefined;
    
    // Create ticket
    const ticket: Omit<SupportTicket, "id" | "created_at" | "updated_at"> = {
      thread_id: threadId,
      contact_id: thread.contact_id,
      subject: thread.subject || 'Nova solicitação de suporte',
      description: firstMessage.content,
      status: 'new',
      priority: 'medium',
      category: 'service_request',
      ...ticketData
    };
    
    return supportService.createTicket(ticket);
  },

  // Notes
  getNotesByTicketId: async (ticketId: string): Promise<SupportNote[]> => {
    return notes
      .filter(note => note.ticket_id === ticketId)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  },

  createNote: async (note: Omit<SupportNote, "id" | "created_at">): Promise<SupportNote> => {
    const newNote: SupportNote = {
      id: `note-${uuidv4()}`,
      ...note,
      created_at: new Date().toISOString()
    };
    notes.push(newNote);
    return newNote;
  },

  // Email specific helpers
  sendEmailReply: async (
    threadId: string, 
    content: string, 
    options?: { 
      cc?: string[]; 
      bcc?: string[]; 
      attachments?: File[]; 
      includeSignature?: boolean
    }
  ): Promise<SupportMessage> => {
    // Simulate sending an email
    return new Promise(async (resolve) => {
      // Create message first
      const message = await supportService.createMessage({
        thread_id: threadId,
        sender_type: 'agent',
        sender_id: 'agent-1',
        content: content
      });
      
      // Add signature if needed
      let finalContent = content;
      if (options?.includeSignature) {
        finalContent += "\n\n--\nEquipe de Suporte OrgManage\n+55 (11) 4567-8900\nsuporte@orgmanage.com";
        
        // Update message with signature
        const messageIndex = messages.findIndex(m => m.id === message.id);
        if (messageIndex !== -1) {
          messages[messageIndex].content = finalContent;
        }
      }
      
      // Process attachments if any
      if (options?.attachments && options.attachments.length > 0) {
        const attachmentPromises = options.attachments.map(async (file) => {
          const attachment = await supportService.uploadAttachment(file);
          return attachment;
        });
        
        const uploadedAttachments = await Promise.all(attachmentPromises);
        
        // Add attachments to the message
        message.attachments = uploadedAttachments;
      }
      
      // Simulate a delay to represent email sending
      setTimeout(() => {
        resolve(message);
      }, 800);
    });
  },
  
  forwardEmail: async (
    originalMessageId: string, 
    recipients: string[], 
    additionalContent: string,
    options?: { 
      cc?: string[]; 
      bcc?: string[]; 
      attachments?: File[] 
    }
  ): Promise<{ success: boolean; message?: string }> => {
    // Find original message
    const originalMessage = messages.find(m => m.id === originalMessageId);
    if (!originalMessage) {
      return { success: false, message: "Mensagem original não encontrada" };
    }
    
    // Simulate forwarding logic
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ success: true, message: "Email encaminhado com sucesso" });
      }, 800);
    });
  },

  // Helper for WhatsApp integration
  getOrCreateContactFromWhatsApp: async (phone: string, name: string, email?: string): Promise<SupportContact> => {
    // Try to find existing contact by phone
    const existingContact = contacts.find(c => c.phone === phone);
    if (existingContact) return existingContact;
    
    // Create new contact
    return supportService.createContact({
      name,
      phone,
      email
    });
  },

  // Helper for Chat integration
  createChatThread: async (contactId: string, firstMessage: string): Promise<{ thread: SupportThread; message: SupportMessage }> => {
    // Create new thread
    const thread = await supportService.createThread({
      contact_id: contactId,
      channel: 'live_chat'
    });
    
    // Add first message
    const message = await supportService.createMessage({
      thread_id: thread.id,
      sender_type: 'contact',
      sender_id: contactId,
      content: firstMessage
    });
    
    return { thread, message };
  },

  // Helper for WhatsApp integration
  createWhatsAppThread: async (contactId: string, firstMessage: string): Promise<{ thread: SupportThread; message: SupportMessage }> => {
    // Create new thread
    const thread = await supportService.createThread({
      contact_id: contactId,
      channel: 'whatsapp'
    });
    
    // Add first message
    const message = await supportService.createMessage({
      thread_id: thread.id,
      sender_type: 'contact',
      sender_id: contactId,
      content: firstMessage
    });
    
    return { thread, message };
  },
  
  // Helper for Email integration
  createEmailThread: async (
    contactId: string, 
    subject: string, 
    content: string, 
    options?: { 
      cc?: string[];
      attachments?: Array<{ filename: string; content: string; type: string }> 
    }
  ): Promise<{ thread: SupportThread; message: SupportMessage }> => {
    // Create new thread
    const thread = await supportService.createThread({
      contact_id: contactId,
      channel: 'email',
      subject: subject
    });
    
    // Convert attachment format if provided
    let attachmentsForMessage: Array<{ id: string; filename: string; url: string; type: string }> | undefined;
    
    if (options?.attachments && options.attachments.length > 0) {
      attachmentsForMessage = options.attachments.map(att => ({
        id: uuidv4(),
        filename: att.filename,
        url: `mock-url-${uuidv4()}`,
        type: att.type
      }));
    }
    
    // Add first message
    const message = await supportService.createMessage({
      thread_id: thread.id,
      sender_type: 'contact',
      sender_id: contactId,
      content: content,
      attachments: attachmentsForMessage
    });
    
    return { thread, message };
  }
};

export default supportService; 